import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';

interface TurnstileApi {
  render: (container: HTMLElement, options: {
    sitekey: string;
    action: string;
    size: 'compact' | 'flexible';
    theme: 'auto';
    callback: (token: string) => void;
    'expired-callback': () => void;
    'error-callback': (errorCode: string) => boolean;
  }) => string;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window { turnstile?: TurnstileApi }
}

let scriptPromise: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    const fail = () => {
      clearTimeout(timer);
      script.remove();
      reject(new Error('無法載入人機驗證，請檢查網路後重試'));
    };
    const timer = window.setTimeout(fail, 15_000);
    script.onerror = fail;
    script.onload = () => {
      clearTimeout(timer);
      if (window.turnstile) resolve(window.turnstile);
      else fail();
    };
    document.head.appendChild(script);
  }).catch((error) => {
    scriptPromise = null;
    throw error;
  });
  return scriptPromise;
}

export function Turnstile({ siteKey, onVerify }: {
  siteKey: string;
  onVerify: (token: string) => void;
}) {
  const container = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<'compact' | 'flexible' | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [message, setMessage] = useState('正在載入人機驗證…');

  useEffect(() => {
    if (!container.current) return;
    const observer = new ResizeObserver(([entry]) => {
      setSize(entry.contentRect.width < 300 ? 'compact' : 'flexible');
    });
    observer.observe(container.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!size || !container.current) return;
    let alive = true;
    let widgetId: string | undefined;
    let api: TurnstileApi | undefined;
    onVerify('');
    setMessage('正在載入人機驗證…');
    loadTurnstile().then((loaded) => {
      if (!alive || !container.current) return;
      api = loaded;
      setMessage('請完成人機驗證');
      widgetId = api.render(container.current, {
        sitekey: siteKey, action: 'login', size, theme: 'auto',
        callback: (token) => {
          if (!alive) return;
          setMessage('');
          onVerify(token);
        },
        'expired-callback': () => {
          if (!alive) return;
          onVerify('');
          setMessage('驗證已過期，請重新驗證');
        },
        'error-callback': (errorCode) => {
          if (!alive) return true;
          console.warn('Turnstile verification error:', errorCode);
          onVerify('');
          setMessage('人機驗證暫時無法完成，請重試');
          return true;
        },
      });
    }).catch((error: Error) => {
      if (alive) setMessage(error.message);
    });
    return () => {
      alive = false;
      if (widgetId !== undefined) api?.remove(widgetId);
    };
  }, [siteKey, size, attempt, onVerify]);

  return (
    <div className="mt-3" aria-label="登入人機驗證">
      <div ref={container} className="flex min-h-[65px] justify-center" />
      {message && (
        <div className="mt-1 flex flex-wrap items-center justify-between gap-x-2 text-xs text-muted-foreground">
          <p role="status" aria-live="polite">{message}</p>
          <Button type="button" variant="link" size="sm" onClick={() => setAttempt((value) => value + 1)}>
            重新驗證
          </Button>
        </div>
      )}
    </div>
  );
}
